import ReactMarkdown, { type ExtraProps } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { MermaidDiagram } from './Mermaid';

const MERMAID = /\blanguage-mermaid\b/;

/** True for a `pre` whose only child is a ```mermaid code block, which renders as a diagram instead. */
const wrapsMermaid = (node: ExtraProps['node']) => {
  const child = node?.children[0];
  return (
    child?.type === 'element' &&
    child.tagName === 'code' &&
    MERMAID.test(([] as unknown[]).concat(child.properties.className ?? []).join(' '))
  );
};

/**
 * Sanitized Markdown (NFR-01, AC-27): raw HTML is skipped, output is sanitized, links open safely and
 * remote images are never fetched automatically; they render as links instead. ```mermaid blocks render as
 * diagrams (JIRA-1).
 */
export function Markdown({ children }: { children: string }) {
  return (
    <div className="markdown">
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeSanitize,
          [rehypeHighlight, { detect: false, plainText: ['mermaid', 'text', 'txt'] }],
        ]}
        components={{
          pre: ({ node, children }) => (wrapsMermaid(node) ? <>{children}</> : <pre>{children}</pre>),
          code: ({ className, children }) =>
            MERMAID.test(className ?? '') ? (
              <MermaidDiagram code={String(children).replace(/\n$/, '')} />
            ) : (
              <code className={className}>{children}</code>
            ),
          a: ({ href, children: c }) => (
            <a href={href} target="_blank" rel="noopener noreferrer nofollow">
              {c}
            </a>
          ),
          img: ({ src, alt }) => (
            <span className="blocked-image">
              [Image not loaded automatically: {alt || 'image'} —{' '}
              <a
                href={typeof src === 'string' ? src : '#'}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                open
              </a>
              ]
            </span>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
